import { useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  ChevronRight,
  Copy,
  Loader2,
  Printer,
  Share2,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import { STRINGS } from '@maintenance-tracker/shared';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { useAuth } from '@/lib/auth';
import { api, ApiError, cn } from '@/lib/utils';

/** Avoid TanStack route-union narrowing when LEGACY_ON is a runtime boolean. */
function appNavigate(path: string) {
  window.location.assign(path);
}

type ProgressStep = { id: string; label: string; status: string };

type Finding = {
  id: string;
  checkId: string;
  status: string;
  category: string;
  severity?: string | null;
  title: string;
  evidence: Array<{ pageUrl?: string; snippet?: string; measurement?: string }>;
  explanation?: string | null;
  whyItMatters?: string | null;
  fixSummary?: string | null;
  fixSteps?: string[];
  kitAction?: string | null;
  effortMinutes?: number | null;
  userState?: string | null;
  source?: string;
  feedback?: string | null;
};

type ReviewDetail = {
  id: string;
  url: string;
  finalUrl?: string | null;
  status: string;
  detectedPlatform?: string | null;
  summaryCounts: {
    needsAttention?: number;
    looksGood?: number;
    couldNotCheck?: number;
    checked?: number;
    total?: number;
    summarySentence?: string;
  };
  summarySentence?: string | null;
  finishedAt?: string | null;
  createdAt?: string;
  shareActive?: boolean;
  shareToken?: string | null;
};

const CATEGORIES = [
  { id: 'clarity', label: 'Clarity' },
  { id: 'contact', label: 'Contact' },
  { id: 'trust', label: 'Trust' },
  { id: 'mobile', label: 'Mobile' },
  { id: 'search', label: 'Get found on Google' },
  { id: 'availability', label: 'Availability' },
] as const;

const QUESTIONS = [
  { key: 'gbp_claimed', label: 'Is your Google Business Profile claimed?' },
  { key: 'has_license', label: 'Do you have a state contractor license you can show?' },
  { key: 'insured', label: 'Are you insured and willing to say so on your site?' },
  { key: 'has_photos', label: 'Do you have recent real photos of your work?' },
] as const;

function hostnameOf(url: string) {
  try {
    return new URL(url.includes('://') ? url : `https://${url}`).hostname;
  } catch {
    return url;
  }
}

function effortLabel(minutes?: number | null) {
  if (!minutes) return null;
  if (minutes <= 15) return `~${minutes} min`;
  if (minutes <= 30) return 'About 30 min';
  return 'About an hour';
}

function statusLabel(status: string) {
  if (status === 'verified_problem') return 'Needs attention';
  if (status === 'verified_ok') return 'Looks good';
  if (status === 'possible_issue') return 'Possible issue';
  return 'Could not check';
}

function statusChipClass(status: string) {
  if (status === 'verified_problem') return 'bg-danger-subtle text-danger';
  if (status === 'verified_ok') return 'bg-success-subtle text-success';
  return 'bg-bg-subtle text-text-secondary';
}

/** Start screen + questionnaire + progress. No auto-crawl on open. */
export function ReviewStartPage() {
  const { user } = useAuth();
  const [url, setUrl] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showQuestions, setShowQuestions] = useState(false);
  const [emailWhenReady, setEmailWhenReady] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState<
    Array<{ id: string; url: string; status: string; finishedAt?: string | null; summaryCounts?: Record<string, number> }>
  >([]);
  const [noWebsite, setNoWebsite] = useState(false);
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);

  useEffect(() => {
    api<{ business: { websiteUrl?: string | null } | null }>('/api/v1/business/me')
      .then((data) => {
        if (data.business?.websiteUrl) setUrl(data.business.websiteUrl);
        else if (data.business && !data.business.websiteUrl) setNoWebsite(true);
      })
      .catch(() => undefined);

    api<{ reviews: typeof history }>('/api/v1/reviews')
      .then((data) => setHistory(data.reviews ?? []))
      .catch(() => undefined);
  }, []);

  const latestCompleted = history.find(
    (r) => r.status === 'completed' || r.status === 'completed_with_limitations',
  );

  const start = async (force = false) => {
    setError('');
    if (!url.trim()) {
      setError('Enter your website URL to start.');
      return;
    }
    setLoading(true);
    try {
      const questionnaire = Object.fromEntries(
        Object.entries(answers).filter(([, v]) => v && v !== 'skip'),
      );
      const res = await api<{ reviewId: string; cached?: boolean }>('/api/v1/reviews', {
        method: 'POST',
        body: JSON.stringify({
          url: url.trim(),
          force,
          emailWhenReady,
          questionnaire: Object.keys(questionnaire).length ? questionnaire : undefined,
        }),
      });
      if (res.cached && !force) {
        appNavigate(`/app/review/${res.reviewId}`);
        return;
      }
      setActiveReviewId(res.reviewId);
    } catch (err) {
      setError((err as ApiError).message || 'Could not start review');
    } finally {
      setLoading(false);
    }
  };

  if (activeReviewId) {
    return (
      <ReviewProgressPanel
        reviewId={activeReviewId}
        emailWhenReady={emailWhenReady}
        onCancel={() => setActiveReviewId(null)}
        onDone={() => appNavigate(`/app/review/${activeReviewId}`)}
      />
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <PageHeader
        title={STRINGS.review.startTitle}
        description={STRINGS.review.startHint}
      />

      {noWebsite && !url && (
        <div className="rounded-xl border border-border bg-bg-surface p-5 text-sm text-text-secondary">
          <p className="font-medium text-text-primary">No website yet</p>
          <p className="mt-1">
            You can still use Quote Requests and Reviews & Project Proof. When you have a site, paste the URL
            here — we will not crawl until you start.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href="/app/quote-requests">
              <Button variant="secondary" size="sm">
                Quote Requests
              </Button>
            </a>
            <a href="/app/reviews-proof">
              <Button variant="secondary" size="sm">
                Reviews & Proof
              </Button>
            </a>
          </div>
        </div>
      )}

      {latestCompleted && (
        <div className="rounded-xl border border-border bg-bg-surface p-5">
          <p className="text-sm text-text-secondary">Latest review</p>
          <p className="mt-1 font-medium text-text-primary">{hostnameOf(latestCompleted.url)}</p>
          <p className="mt-1 text-xs text-text-muted">
            {latestCompleted.finishedAt
              ? new Date(latestCompleted.finishedAt).toLocaleString()
              : latestCompleted.status}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={`/app/review/${latestCompleted.id}`}>
              <Button size="sm">View report</Button>
            </a>
            <Button size="sm" variant="secondary" onClick={() => setShowQuestions(true)}>
              Run a new review
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-4 rounded-xl border border-border bg-bg-surface p-5">
        <Input
          label="Website URL"
          placeholder="https://yourcompany.com"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setNoWebsite(false);
          }}
          helper="Takes about 2 minutes. We only read your public pages. Nothing on your site is changed."
        />

        {!showQuestions ? (
          <div className="flex flex-wrap gap-2">
            <Button loading={loading} onClick={() => setShowQuestions(true)}>
              Review my website
            </Button>
            <Button variant="ghost" onClick={() => start(false)} loading={loading}>
              Skip questions & start
            </Button>
          </div>
        ) : (
          <div className="space-y-4 border-t border-border pt-4">
            <p className="text-sm text-text-secondary">
              Optional quick questions (you can skip). Answers are labeled owner-reported.
            </p>
            {QUESTIONS.map((q) => (
              <fieldset key={q.key} className="space-y-2">
                <legend className="text-[13px] font-medium text-text-primary">{q.label}</legend>
                <div className="flex flex-wrap gap-2">
                  {['yes', 'no', 'not_sure'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setAnswers((a) => ({ ...a, [q.key]: opt }))}
                      className={cn(
                        'rounded-lg border px-3 py-1.5 text-sm capitalize',
                        answers[q.key] === opt
                          ? 'border-brand bg-brand/10 text-brand'
                          : 'border-border text-text-secondary hover:bg-bg-subtle',
                      )}
                    >
                      {opt.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </fieldset>
            ))}
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={emailWhenReady}
                onChange={(e) => setEmailWhenReady(e.target.checked)}
              />
              Email me at {user?.user?.email} when ready
            </label>
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex flex-wrap gap-2">
              <Button loading={loading} onClick={() => start(Boolean(latestCompleted))}>
                Start review
              </Button>
              <Button variant="ghost" onClick={() => setShowQuestions(false)}>
                Back
              </Button>
            </div>
          </div>
        )}
        {error && !showQuestions && <p className="text-sm text-danger">{error}</p>}
      </div>

      {history.length > 1 && (
        <HistoryList
          reviews={history}
          onCompare={(a, b) => appNavigate(`/app/review/${a}?compare=${encodeURIComponent(b)}`)}
        />
      )}
    </div>
  );
}

function ReviewProgressPanel({
  reviewId,
  emailWhenReady,
  onCancel,
  onDone,
}: {
  reviewId: string;
  emailWhenReady: boolean;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [steps, setSteps] = useState<ProgressStep[]>([]);
  const [status, setStatus] = useState('queued');
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const data = await api<{
          review: { status: string };
          progress: { steps: ProgressStep[]; error?: string };
        }>(`/api/v1/reviews/${reviewId}/status`);
        if (!alive) return;
        setStatus(data.review.status);
        setSteps(data.progress.steps ?? []);
        if (data.progress.error) setError(data.progress.error);
        if (
          data.review.status === 'completed' ||
          data.review.status === 'completed_with_limitations' ||
          data.review.status === 'failed'
        ) {
          if (data.review.status !== 'failed') onDone();
        }
      } catch (err) {
        if (alive) setError((err as ApiError).message);
      }
    };
    poll();
    const t = setInterval(poll, 1500);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [reviewId, onDone]);

  const cancel = async () => {
    setCancelling(true);
    try {
      await api(`/api/v1/reviews/${reviewId}/cancel`, { method: 'POST' });
      onCancel();
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <PageHeader title="Reviewing your website" description="Named stages — no fake percentages." />
      <ol className="space-y-3">
        {steps.map((s) => (
          <li key={s.id} className="flex items-center gap-3 text-sm">
            <StepIcon status={s.status} />
            <span
              className={cn(
                s.status === 'pending' && 'text-text-muted',
                s.status === 'running' && 'text-text-primary font-medium',
                s.status === 'done' && 'text-text-primary',
                (s.status === 'failed' || s.status === 'skipped') && 'text-text-muted',
              )}
            >
              {s.label}
              {s.status === 'skipped' && ' — Couldn\'t check'}
            </span>
          </li>
        ))}
      </ol>
      {emailWhenReady && (
        <p className="text-xs text-text-muted">You can leave — we&apos;ll email you when it&apos;s ready.</p>
      )}
      {error && status === 'failed' && (
        <div className="rounded-xl border border-danger/30 bg-danger-subtle p-4 text-sm text-danger">
          {error.includes('block') || error.includes('403')
            ? STRINGS.review.botBlocked
            : error}
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" loading={cancelling} onClick={cancel}>
          Cancel
        </Button>
        {status === 'failed' && (
          <Button variant="ghost" onClick={onCancel}>
            Back
          </Button>
        )}
      </div>
    </div>
  );
}

function StepIcon({ status }: { status: string }) {
  if (status === 'running') return <Loader2 className="h-4 w-4 animate-spin text-brand" />;
  if (status === 'done') return <Check className="h-4 w-4 text-success" />;
  if (status === 'failed' || status === 'skipped')
    return <span className="h-4 w-4 rounded-full bg-bg-subtle" aria-hidden />;
  return <span className="h-4 w-4 rounded-full border border-border" aria-hidden />;
}

function HistoryList({
  reviews,
  onCompare,
}: {
  reviews: Array<{
    id: string;
    url: string;
    status: string;
    finishedAt?: string | null;
    summaryCounts?: Record<string, number>;
  }>;
  onCompare: (a: string, b: string) => void;
}) {
  const completed = reviews.filter(
    (r) => r.status === 'completed' || r.status === 'completed_with_limitations',
  );
  if (completed.length < 2) return null;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-text-primary">History</h3>
      <ul className="divide-y divide-border rounded-xl border border-border bg-bg-surface">
        {completed.slice(0, 10).map((r, i) => (
          <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
            <div>
              <a href={`/app/review/${r.id}`} className="font-medium text-brand hover:underline">
                {hostnameOf(r.url)}
              </a>
              <p className="text-xs text-text-muted">
                {r.finishedAt ? new Date(r.finishedAt).toLocaleDateString() : '—'} ·{' '}
                {r.summaryCounts?.needsAttention ?? 0} need attention
              </p>
            </div>
            {i < completed.length - 1 && (
              <Button size="sm" variant="ghost" onClick={() => onCompare(r.id, completed[i + 1].id)}>
                Compare
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReviewDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const search = useSearch({ strict: false }) as { compare?: string };
  const { user } = useAuth();
  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [topPriorities, setTopPriorities] = useState<Finding[]>([]);
  const [tab, setTab] = useState<string>('clarity');
  const [reportMode, setReportMode] = useState<'report' | 'fixkit'>('report');
  const [selected, setSelected] = useState<Finding | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState('');
  const [referralDismissed, setReferralDismissed] = useState(false);
  const [compare, setCompare] = useState<{
    fixed: string[];
    newProblems: string[];
    unchanged: string[];
    titles: Record<string, string>;
  } | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api<{
        review: ReviewDetail;
        findings: Finding[];
        topPriorities: Finding[];
      }>(`/api/v1/reviews/${id}`);
      setReview(data.review);
      setFindings(data.findings);
      setTopPriorities(data.topPriorities);
      if (data.review.shareToken) {
        setShareUrl(`${window.location.origin}/share/${data.review.shareToken}`);
      }
      const firstCat = CATEGORIES.find((c) =>
        data.findings.some((f) => f.category === c.id && f.status === 'verified_problem'),
      );
      if (firstCat) setTab(firstCat.id);
    } catch (err) {
      setError((err as ApiError).message || 'Could not load report');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!search?.compare || !id) return;
    api<{
      fixed: string[];
      newProblems: string[];
      unchanged: string[];
      titles: Record<string, string>;
    }>(`/api/v1/reviews/${id}/compare?with=${encodeURIComponent(search.compare)}`)
      .then(setCompare)
      .catch(() => undefined);
  }, [id, search?.compare]);

  const counts = review?.summaryCounts ?? {};
  const readinessChecked = counts.looksGood ?? 0;
  const readinessTotal = (counts.looksGood ?? 0) + (counts.needsAttention ?? 0);

  const byCategory = useMemo(() => {
    const list = findings.filter((f) => f.category === tab);
    const needs = list.filter((f) => f.status === 'verified_problem');
    const cnc = list.filter((f) => f.status === 'could_not_check' || f.status === 'possible_issue');
    const ok = list.filter((f) => f.status === 'verified_ok');
    return { needs, cnc, ok };
  }, [findings, tab]);

  const createShare = async () => {
    const res = await api<{ url: string }>(`/api/v1/reviews/${id}/share`, { method: 'POST' });
    setShareUrl(res.url);
  };

  const revokeShare = async () => {
    await api(`/api/v1/reviews/${id}/share`, { method: 'DELETE' });
    setShareUrl('');
  };

  const patchFinding = async (
    findingId: string,
    body: { userState?: string; dismissReason?: string; feedback?: 'up' | 'down' },
  ) => {
    const res = await api<{ finding: Finding }>(
      `/api/v1/reviews/${id}/findings/${findingId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    setFindings((prev) => prev.map((f) => (f.id === findingId ? { ...f, ...res.finding } : f)));
    if (selected?.id === findingId) setSelected({ ...selected, ...res.finding });
  };

  const referralLink = user?.user?.referralCode
    ? `${window.location.origin}/signup?ref=${user.user.referralCode}`
    : `${window.location.origin}/signup`;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="mx-auto max-w-lg text-center">
        <p className="text-danger">{error || 'Report not found'}</p>
        <a href="/app/review" className="mt-4 inline-flex">
          <Button variant="secondary">Back</Button>
        </a>
      </div>
    );
  }

  const domain = hostnameOf(review.finalUrl || review.url);

  return (
    <div className="mx-auto max-w-3xl space-y-8 print:max-w-none">
      <PageHeader
        title={domain}
        description={
          review.finishedAt
            ? `Reviewed ${new Date(review.finishedAt).toLocaleString()}`
            : review.status
        }
        actions={
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => appNavigate('/app/review')}
            >
              Re-run review
            </Button>
            <Button size="sm" variant="secondary" onClick={() => window.print()}>
              <Printer className="h-4 w-4" /> Print / PDF
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                if (!shareUrl) await createShare();
                else await navigator.clipboard.writeText(shareUrl);
              }}
            >
              <Share2 className="h-4 w-4" /> {shareUrl ? 'Copy share link' : 'Share link'}
            </Button>
            {shareUrl && (
              <Button size="sm" variant="ghost" onClick={revokeShare}>
                Revoke
              </Button>
            )}
          </div>
        }
      />

      {shareUrl && (
        <p className="print:hidden text-xs text-text-muted">
          Private unlisted link (revocable): <code className="break-all">{shareUrl}</code>
        </p>
      )}

      <div className="flex gap-1 border-b border-border print:hidden">
        <button
          type="button"
          onClick={() => setReportMode('report')}
          className={cn(
            'px-3 py-2 text-sm font-medium',
            reportMode === 'report'
              ? 'border-b-2 border-brand text-brand'
              : 'text-text-secondary hover:text-text-primary',
          )}
        >
          Report
        </button>
        <button
          type="button"
          onClick={() => setReportMode('fixkit')}
          className={cn(
            'px-3 py-2 text-sm font-medium',
            reportMode === 'fixkit'
              ? 'border-b-2 border-brand text-brand'
              : 'text-text-secondary hover:text-text-primary',
          )}
        >
          Fix kit
        </button>
      </div>

      {reportMode === 'fixkit' && (
        <FixKitTab reviewId={id} priorities={topPriorities} />
      )}

      {reportMode === 'report' && (
      <>
      <div className="flex flex-wrap gap-2">
        <Chip label="Needs attention" value={counts.needsAttention ?? 0} tone="danger" />
        <Chip label="Looks good" value={counts.looksGood ?? 0} tone="success" />
        <Chip label="Could not check" value={counts.couldNotCheck ?? 0} tone="muted" />
      </div>

      {readinessTotal > 0 && (
        <div>
          <p className="text-sm font-medium text-text-primary">
            {readinessChecked} of {readinessTotal} checks passed
          </p>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-bg-subtle">
            <div
              className="h-full rounded-full bg-brand"
              style={{ width: `${Math.round((readinessChecked / readinessTotal) * 100)}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-text-muted">Not a vanity score — only completed checks count.</p>
        </div>
      )}

      <p className="text-[15px] text-text-secondary">
        {review.summarySentence ||
          (counts as { summarySentence?: string }).summarySentence ||
          'Review complete.'}
      </p>

      {compare && (
        <div className="rounded-xl border border-border bg-bg-surface p-4 text-sm">
          <h3 className="font-semibold text-text-primary">Compared to previous review</h3>
          <ul className="mt-2 space-y-1 text-text-secondary">
            <li>Fixed: {compare.fixed.map((id) => compare.titles[id] || id).join('; ') || 'None'}</li>
            <li>
              New: {compare.newProblems.map((id) => compare.titles[id] || id).join('; ') || 'None'}
            </li>
            <li>Still open: {compare.unchanged.length}</li>
          </ul>
        </div>
      )}

      <section>
        <h2 className="text-lg font-semibold text-text-primary">Your top 3 priorities</h2>
        <div className="mt-3 space-y-3">
          {topPriorities.length === 0 && (
            <p className="text-sm text-text-secondary">No verified problems with evidence to prioritize.</p>
          )}
          {topPriorities.map((f) => (
            <div
              key={f.id}
              className="flex flex-col gap-3 rounded-xl border border-border bg-bg-surface p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <button type="button" className="text-left" onClick={() => setSelected(f)}>
                <p className="font-medium text-text-primary">{f.title}</p>
                <p className="mt-1 text-sm text-text-secondary">{f.whyItMatters}</p>
                {effortLabel(f.effortMinutes) && (
                  <span className="mt-2 inline-block rounded-md bg-bg-subtle px-2 py-0.5 text-xs text-text-muted">
                    {effortLabel(f.effortMinutes)}
                  </span>
                )}
              </button>
              <a
                href={`/app/improvements?findingId=${encodeURIComponent(f.id)}&reviewId=${encodeURIComponent(id)}`}
                className="shrink-0"
              >
                <Button size="sm">Fix this</Button>
              </a>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex gap-1 overflow-x-auto border-b border-border pb-px print:flex-wrap">
          {CATEGORIES.map((c) => {
            const n = findings.filter(
              (f) => f.category === c.id && f.status === 'verified_problem',
            ).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setTab(c.id)}
                className={cn(
                  'shrink-0 px-3 py-2 text-sm font-medium',
                  tab === c.id
                    ? 'border-b-2 border-brand text-brand'
                    : 'text-text-secondary hover:text-text-primary',
                )}
              >
                {c.label}
                {n > 0 ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>
        <div className="mt-4 space-y-2">
          {[...byCategory.needs, ...byCategory.cnc].map((f) => (
            <FindingRow key={f.id} finding={f} onOpen={() => setSelected(f)} />
          ))}
          {byCategory.ok.length > 0 && (
            <details className="rounded-lg border border-border">
              <summary className="cursor-pointer px-4 py-3 text-sm text-text-secondary">
                Looks good ({byCategory.ok.length})
              </summary>
              <div className="space-y-1 border-t border-border px-2 py-2">
                {byCategory.ok.map((f) => (
                  <FindingRow key={f.id} finding={f} onOpen={() => setSelected(f)} />
                ))}
              </div>
            </details>
          )}
        </div>
      </section>

      {review.detectedPlatform && (
        <p className="text-sm text-text-secondary">
          Your site <span className="font-medium">appears to be</span> built on{' '}
          {review.detectedPlatform}.
        </p>
      )}

      <section className="rounded-xl border border-border bg-bg-muted/40 p-4">
        <h3 className="text-sm font-semibold text-text-primary">What we did not check</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-text-secondary">
          <li>Google rankings or traffic estimates</li>
          <li>Private Google Business Profile details</li>
          <li>Whether forms actually deliver email</li>
          <li>Competitor websites</li>
          <li>Content of customer reviews</li>
        </ul>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-text-primary">Next steps</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href="/app/improvements">
            <Button size="sm">Open Improvement Kit</Button>
          </a>
          <a href="/app/quote-requests">
            <Button size="sm" variant="secondary">
              Make it easier to ask for a quote
            </Button>
          </a>
          <a href="/app/reviews-proof">
            <Button size="sm" variant="secondary">
              Show your real work
            </Button>
          </a>
        </div>
      </section>

      {!referralDismissed && (
        <div className="relative rounded-xl border border-border bg-bg-surface p-5 print:hidden">
          <button
            type="button"
            className="absolute right-3 top-3 text-text-muted hover:text-text-primary"
            onClick={() => setReferralDismissed(true)}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
          <h3 className="font-semibold text-text-primary">
            Know another contractor who&apos;d want a free website check-up?
          </h3>
          <p className="mt-1 break-all text-sm text-text-secondary">{referralLink}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigator.clipboard.writeText(referralLink)}
            >
              <Copy className="h-4 w-4" /> Copy link
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (navigator.share) {
                  void navigator.share({
                    title: 'Free website check-up',
                    url: referralLink,
                  });
                } else {
                  void navigator.clipboard.writeText(referralLink);
                }
              }}
            >
              Share
            </Button>
            <a
              href={`mailto:?subject=${encodeURIComponent('Free website check-up for contractors')}&body=${encodeURIComponent(`Thought you might want this free website check-up:\n\n${referralLink}\n`)}`}
            >
              <Button size="sm" variant="ghost">
                Email a friend
              </Button>
            </a>
          </div>
        </div>
      )}

      <section className="print:hidden">
        <p className="text-sm font-medium text-text-primary">Was this report accurate?</p>
        {!feedbackSent ? (
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                await api(`/api/v1/reviews/${id}/feedback`, {
                  method: 'POST',
                  body: JSON.stringify({ rating: 'up' }),
                });
                setFeedbackSent(true);
              }}
            >
              <ThumbsUp className="h-4 w-4" /> Yes
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                await api(`/api/v1/reviews/${id}/feedback`, {
                  method: 'POST',
                  body: JSON.stringify({ rating: 'down' }),
                });
                setFeedbackSent(true);
              }}
            >
              <ThumbsDown className="h-4 w-4" /> Needs work
            </Button>
          </div>
        ) : (
          <p className="mt-2 text-sm text-text-secondary">Thanks — that helps us improve.</p>
        )}
      </section>
      </>
      )}

      <FindingDrawer
        finding={selected}
        platform={review.detectedPlatform}
        onClose={() => setSelected(null)}
        onPatch={patchFinding}
        reviewId={id}
      />
    </div>
  );
}

function FixKitTab({
  reviewId,
  priorities,
}: {
  reviewId: string;
  priorities: Finding[];
}) {
  const cards = [
    { action: 'service_page', title: 'Improve my service page', outcome: 'Clear service copy' },
    { action: 'trust_section', title: 'Improve my trust section', outcome: 'Credentials you truly hold' },
    { action: 'contact_section', title: 'Improve my contact section', outcome: 'Obvious next step on mobile' },
    { action: 'technical_seo', title: 'Technical SEO fixes', outcome: 'Titles, meta, headings, JSON-LD' },
    { action: 'seo_plan', title: 'My SEO action plan', outcome: 'This week / Next / Later' },
  ] as const;

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-lg font-semibold text-text-primary">Recommended for your website</h2>
        <div className="mt-3 space-y-2">
          {priorities.length === 0 && (
            <p className="text-sm text-text-secondary">
              No open priorities yet. Start any improvement below, or finish a review first.
            </p>
          )}
          {priorities.slice(0, 3).map((f) => (
            <a
              key={f.id}
              href={`/app/improvements?findingId=${encodeURIComponent(f.id)}&reviewId=${encodeURIComponent(reviewId)}${f.kitAction ? `&action=${encodeURIComponent(f.kitAction === 'checklist' ? 'seo_plan' : f.kitAction)}` : ''}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-border bg-bg-surface p-4 shadow-card hover:border-brand/30"
            >
              <div>
                <p className="font-medium text-text-primary">{f.title}</p>
                {f.whyItMatters && (
                  <p className="mt-1 text-sm text-text-secondary">{f.whyItMatters}</p>
                )}
              </div>
              <Button size="sm">Fix this</Button>
            </a>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-text-primary">Create an improvement</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {cards.map((c) => (
            <a
              key={c.action}
              href={`/app/improvements?action=${c.action}&reviewId=${encodeURIComponent(reviewId)}`}
              className="rounded-xl border border-border bg-bg-surface p-4 shadow-card hover:border-brand/30"
            >
              <p className="font-medium text-text-primary">{c.title}</p>
              <p className="mt-1 text-sm text-text-secondary">{c.outcome}</p>
            </a>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-dashed border-border bg-bg-muted/30 p-5">
        <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Coming soon</p>
        <h3 className="mt-1 font-semibold text-text-primary">Coming soon: smarter website help</h3>
        <p className="mt-2 text-sm text-text-secondary">
          Search Console, Analytics, and AI drafting — join early access from the Improvement Kit.
        </p>
        <a href="/app/improvements" className="mt-3 inline-flex">
          <Button size="sm" variant="secondary">
            Open Fix kit
          </Button>
        </a>
      </section>
    </div>
  );
}

function Chip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'danger' | 'success' | 'muted';
}) {
  const toneClass =
    tone === 'danger'
      ? 'bg-danger-subtle text-danger'
      : tone === 'success'
        ? 'bg-success-subtle text-success'
        : 'bg-bg-subtle text-text-secondary';
  return (
    <span className={cn('inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium', toneClass)}>
      <span className="text-lg font-semibold tabular-nums">{value}</span>
      {label}
    </span>
  );
}

function FindingRow({ finding, onOpen }: { finding: Finding; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-bg-subtle"
    >
      <span className={cn('rounded-md px-2 py-0.5 text-[11px] font-medium', statusChipClass(finding.status))}>
        {statusLabel(finding.status)}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">{finding.title}</span>
      {finding.source === 'owner_reported' && (
        <span className="text-[11px] text-text-muted">Owner-reported</span>
      )}
      <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" />
    </button>
  );
}

function FindingDrawer({
  finding,
  platform,
  onClose,
  onPatch,
  reviewId,
}: {
  finding: Finding | null;
  platform?: string | null;
  onClose: () => void;
  onPatch: (
    id: string,
    body: { userState?: string; dismissReason?: string; feedback?: 'up' | 'down' },
  ) => Promise<void>;
  reviewId: string;
}) {
  const [whyOpen, setWhyOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [showDismiss, setShowDismiss] = useState(false);

  useEffect(() => {
    setWhyOpen(false);
    setShowDismiss(false);
    setDismissReason('');
  }, [finding?.id]);

  if (!finding) return null;

  return (
    <Drawer
      open={Boolean(finding)}
      onClose={onClose}
      title={finding.title}
      subtitle={
        <span className={cn('rounded-md px-2 py-0.5 text-[11px] font-medium', statusChipClass(finding.status))}>
          {statusLabel(finding.status)}
          {finding.source === 'owner_reported' ? ' · Owner-reported' : ''}
        </span>
      }
      footer={
        <div className="flex flex-wrap gap-2">
          {finding.kitAction && (
            <a
              href={`/app/improvements?findingId=${encodeURIComponent(finding.id)}&reviewId=${encodeURIComponent(reviewId)}`}
            >
              <Button size="sm">Fix this with a ready-made draft</Button>
            </a>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onPatch(finding.id, { userState: 'done' })}
          >
            Mark as done
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowDismiss(true)}>
            Not relevant
          </Button>
        </div>
      }
    >
      <div className="space-y-5 text-sm">
        <section>
          <h3 className="font-semibold text-text-primary">What we saw</h3>
          <ul className="mt-2 space-y-2">
            {(Array.isArray(finding.evidence) ? finding.evidence : []).map((e, i) => (
              <li key={i} className="rounded-lg bg-bg-muted px-3 py-2 text-text-secondary">
                {e.pageUrl && (
                  <a href={e.pageUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                    {e.pageUrl}
                  </a>
                )}
                {e.snippet && <p className="mt-1 font-mono text-xs">{e.snippet}</p>}
                {e.measurement && <p className="mt-1">{e.measurement}</p>}
              </li>
            ))}
          </ul>
        </section>
        {finding.whyItMatters && (
          <section>
            <h3 className="font-semibold text-text-primary">Why this matters</h3>
            <p className="mt-1 text-text-secondary">{finding.whyItMatters}</p>
          </section>
        )}
        {(finding.fixSummary || (finding.fixSteps && finding.fixSteps.length > 0)) && (
          <section>
            <h3 className="font-semibold text-text-primary">How to fix it</h3>
            {finding.fixSummary && <p className="mt-1 text-text-secondary">{finding.fixSummary}</p>}
            {platform && (
              <p className="mt-1 text-xs text-text-muted">Tailored for sites that appear to be {platform}.</p>
            )}
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-text-secondary">
              {(finding.fixSteps ?? []).map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </section>
        )}
        <button
          type="button"
          className="text-sm font-medium text-brand hover:underline"
          onClick={() => setWhyOpen((v) => !v)}
        >
          Why am I seeing this?
        </button>
        {whyOpen && (
          <p className="text-text-secondary">
            Check <code className="text-xs">{finding.checkId}</code> looks at public pages we could fetch. Ambiguous
            or unavailable results never become problems. Marking done does not skip the check on the next review —
            if it still fails, you&apos;ll see &quot;Still needs attention.&quot;
          </p>
        )}
        {showDismiss && (
          <div className="space-y-2 rounded-lg border border-border p-3">
            <p className="font-medium">Why is this not relevant?</p>
            {["I don't offer that", 'I already fixed it', 'Other'].map((r) => (
              <button
                key={r}
                type="button"
                className={cn(
                  'block w-full rounded-lg border px-3 py-2 text-left',
                  dismissReason === r ? 'border-brand' : 'border-border',
                )}
                onClick={() => setDismissReason(r)}
              >
                {r}
              </button>
            ))}
            <Button
              size="sm"
              disabled={!dismissReason}
              onClick={async () => {
                await onPatch(finding.id, {
                  userState: 'dismissed',
                  dismissReason,
                });
                setShowDismiss(false);
                onClose();
              }}
            >
              Confirm
            </Button>
          </div>
        )}
        <div className="flex gap-2 border-t border-border pt-4">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onPatch(finding.id, { feedback: 'up' })}
          >
            <ThumbsUp className="h-4 w-4" /> Accurate
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onPatch(finding.id, { feedback: 'down' })}
          >
            <ThumbsDown className="h-4 w-4" /> Not accurate
          </Button>
          <a href="/app?help=1">
            <Button size="sm" variant="ghost">
              Ask for help
            </Button>
          </a>
        </div>
      </div>
    </Drawer>
  );
}

/** Private unlisted share view (noindex). */
export function ShareReportPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const [data, setData] = useState<{
    review: {
      domain: string;
      finishedAt?: string | null;
      summaryCounts: Record<string, number>;
      detectedPlatform?: string | null;
    };
    findings: Finding[];
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    type SharePayload = NonNullable<typeof data>;
    api<SharePayload>(`/api/v1/share/${token}`)
      .then(setData)
      .catch((err) => setError((err as ApiError).message));
    return () => {
      document.head.removeChild(meta);
    };
  }, [token]);

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <p className="text-danger">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-10">
      <p className="text-xs uppercase tracking-wide text-text-muted">Private shared report · noindex</p>
      <h1 className="text-2xl font-semibold">{data.review.domain}</h1>
      <div className="flex flex-wrap gap-2">
        <Chip label="Needs attention" value={data.review.summaryCounts.needsAttention ?? 0} tone="danger" />
        <Chip label="Looks good" value={data.review.summaryCounts.looksGood ?? 0} tone="success" />
        <Chip label="Could not check" value={data.review.summaryCounts.couldNotCheck ?? 0} tone="muted" />
      </div>
      <ul className="space-y-2">
        {data.findings
          .filter((f) => f.status === 'verified_problem')
          .map((f) => (
            <li key={f.id} className="rounded-lg border border-border p-3 text-sm">
              <p className="font-medium">{f.title}</p>
              <p className="mt-1 text-text-secondary">{f.explanation}</p>
            </li>
          ))}
      </ul>
    </div>
  );
}
